import { ProfileUtils } from "abweb/utils/profile/ProfileUtils";
// import {VersionUtils} from "abweb/utils/VersionUtils";
import { action } from "mobx";

/**
 * ModuleLoaderUtils - primarily handles dynamic loading of content (modules, json, text) from the app server.
 * It also verifies that dynamically loaded modules are in the bundle (for systemjs), and profiles the dynamic loading
 * time of the requested entity.  Use the below MOduleLoaderUtils.createImportSpec helper function
 * to create IImportSpec instances that enforce that exportName is valid for the given import statement.
 * However, if you do define a constant (readonly) import spec, you are better off not specifying
 * the IImportSpec type at all so that it can validate the ModT and ExportT bound types (ensuring that exportName
 * is in the module).
 */

export class ModuleLoaderUtils {
  constructor() {}

  /**
   * Enforces passed in exportName is contained within the dynamically loaded module.
   * Useful when defining an import spec in an array, where the array has to have ModT=any, ExportT=any as items in array have different values.
   * @param importModuleFn
   * @param exportName
   */
  static createImportSpec(importModuleFn, exportName) {
    return {
      importModuleFn,
      exportName
    };
  }

  /**
   * This method should only be used by *AppLoaders that take props specifying which app class to load.
   * All other dynamic imports should use the ModuleLoaderUtils.import(ImportRegistry.Foo) instead as it
   * is safer.
   * @param {string} importModuleFn import 'webapp/' relative path of module (does NOT contain 'webapp/')
   * @param {string} exportName name of exported member of module.  Use 'default' if want the default export
   * @param {string} modProfileId Used in profiling stats to identify module being loaded (probably the exportName)
   * @return {Promise<ExportT>} exported member of module
   */
  static async loadExport(importModuleFn, exportName, modProfileId) {
    modProfileId = modProfileId ?? exportName;
    const mod = await ModuleLoaderUtils.importModule(importModuleFn, modProfileId);
    return mod[exportName];
  }

  /**
   * Helper function to support IImportSpec declarative data (json-like) for module loading.
   * If exportSpec.exportName is not provided, it will load default export (fairly rare).
   * @param {IImportSpec} importSpec - contains importModuleFn and optional exportName.  If exportName is not specified,
   * the module must have a default export (fairly rare).
   * @return {Promise<ExportT>} exported member of module when exportName provided, else the defaultExport of the module
   */
  static import(importSpec) {
    return ModuleLoaderUtils.loadExport(importSpec.importModuleFn, importSpec.exportName);
  }

  /**
   * Import a module, typically for the side-effect
   * @param importModuleFn
   * @param profileId Will be used in profiling stats,
   * and is typically the filename of the module being loaded by the importModuleFn
   */
  static importModule(importModuleFn, profileId) {
    return ProfileUtils.elapsedAsync("ModuleLoaderUtils.importModule: " + profileId, importModuleFn);
  }

  /**
   * Helper function that invokes onExport callback (wrapped in an 'action' scope)
   * on exported member
   * @param {IImportSpec} importSpec
   * @param {(newVal: ExportT) => void} onImport
   * @return {Promise<void>}
   */
  static async importAction(importSpec, onImport) {
    const val = await ModuleLoaderUtils.import(importSpec);
    return action(onImport)(val); // equivalent to action((clazz) => onChange(clazz))
  }

  // loadJson - no more callers, use webpack equivalent for files known at build time:
  //     import logJson from "abweb/assets/log/log.json";

  /**
   * Helper method to support imports that use webpack "!text" suffix.  webpack actually translates this into loading
   * a module that has a 'default' export of type string.  This method retrieves the text string from the
   * returned 'default' member of the retrieved module and returns it.
   */
  static async loadText(importTextFn) {
    const mod = await importTextFn();
    return mod.default;
  }
}
//# sourceMappingURL=ModuleLoaderUtils.js.map
